/**
 * Activity metadata containing player and user information.
 */
interface ActivityMetadata {
  roles?: string[];
  teamName?: string;
  team?: string;
  roleName?: string;
  userTitle?: string;
  notePreview?: string;
  userName?: string;
}

/**
 * Derive the report type label from activity metadata.
 * When the metadata contains a team name the report is match-linked,
 * otherwise it is a general (non-match) report.
 * @param data - Activity metadata
 * @returns "Match Report" or "General Report"
 */
const getReportTypeLabel = (data: ActivityMetadata): string =>
  data?.teamName || data?.team ? "Match Report" : "General Report";

/**
 * Formats activity subtitle by combining left and right text with a delimiter.
 * @param left - Left side text (e.g., player roles)
 * @param right - Right side text (e.g., team name)
 * @param delim - Delimiter to use between left and right (defaults to "·")
 * @returns Formatted subtitle string
 */
const formatSubtitle = (
  left?: string,
  right?: string,
  delim?: string
): string => {
  const separator = delim || "·";
  if (left && right) return `${left} ${separator} ${right}`;
  if (left) return left;
  if (right) return right;
  return "";
};

/**
 * Activity types whose body arrives server-rendered and is displayed
 * verbatim (SE-6988). Dispatching them through the metadata handlers would
 * hit the unknown-type fallback and render a spurious "No Team".
 */
const SERVER_RENDERED_BODY_TYPES = new Set(["MATCH_REPORT_SUBMITTED"]);

const handlers: Record<string, (data: ActivityMetadata) => string> = {
  PLAYER_ADDED_TO_WATCHLIST: data =>
    formatSubtitle(data?.roles?.join(", "), data?.teamName || "No Team"),
  PLAYER_REMOVED_FROM_WATCHLIST: data =>
    formatSubtitle(data?.roles?.join(", "), data?.teamName || "No Team"),
  PLAYER_RATING_UPDATED: data =>
    formatSubtitle(data?.roles?.join(", "), data?.teamName || "No Team"),
  PLAYER_ANALYSIS_UPDATED: data =>
    formatSubtitle(data?.roles?.join(", "), data?.teamName || "No Team"),
  SCOUT_REPORT_CREATED: data =>
    formatSubtitle(data?.roles?.join(", "), getReportTypeLabel(data)),
  SCOUT_REPORT_UPDATED: data =>
    formatSubtitle(data?.roles?.join(", "), getReportTypeLabel(data)),
  SCOUT_REPORT_ARCHIVED: data =>
    formatSubtitle(data?.roles?.join(", "), getReportTypeLabel(data)),
  SCOUT_REPORT_SHARED: data =>
    formatSubtitle(data?.roles?.join(", "), getReportTypeLabel(data)),
  CONTENT_SHARED: data =>
    formatSubtitle(data?.roles?.join(", "), getReportTypeLabel(data)),
  NOTE_SHARED: data => data?.notePreview || "",
  NOTE_VISIBILITY_CHANGED: () => "",
  KANBAN_BOARD_VISIBILITY_CHANGED: () => "",
  USER_JOINED_ORGANIZATION: data =>
    formatSubtitle(data?.roleName, data?.userTitle, "-"),
  USER_LEFT_ORGANIZATION: data =>
    formatSubtitle(data?.roleName, data?.userTitle, "-"),
  NOTE_CREATED: data => data?.notePreview || "",
  USER_SIGNED_IN: () => "",
  USER_SIGNED_OUT: () => "",
  MANUAL_PLAYER_CREATED: data => data?.teamName || "",
  MANUAL_PLAYER_UPDATED: data => data?.teamName || "",
  MANUAL_PLAYER_DELETED: data => data?.teamName || "",
  MANUAL_PLAYER_RESTORED: data => data?.teamName || "",
  MANUAL_PLAYER_DATA_TRANSFERRED: () => "",
};

/**
 * Gets activity body content based on activity type and metadata.
 * @remarks
 * Formats activity body content by dispatching to type-specific handlers.
 * Server-rendered body types (SE-6988) return the server body verbatim,
 * with " · by {actorName}" appended when the actor is known.
 * Falls back to PLAYER_ADDED_TO_WATCHLIST handler for unknown activity types.
 * @param activityType - The activity type code (e.g., "PLAYER_ADDED_TO_WATCHLIST")
 * @param metadata - Activity metadata containing player and user information
 * @param serverBody - The server-rendered activity body, displayed verbatim
 * for server-rendered body types
 * @param actorName - Display name of the acting user (backend-computed,
 * SE-7176 — never derived from email); falls back to metadata.userName
 * when absent (e.g. activity.user is null)
 * @returns Formatted body content string
 */
export const getBodyContent = (
  activityType: string,
  metadata: ActivityMetadata | Record<string, unknown> | null | undefined,
  serverBody?: string | null,
  actorName?: string | null
): string => {
  if (SERVER_RENDERED_BODY_TYPES.has(activityType)) {
    const body = serverBody?.trim() || "";
    const actor =
      actorName?.trim() ||
      ((metadata ?? {}) as ActivityMetadata).userName?.trim() ||
      "";
    if (!actor) return body;
    return body ? `${body} · by ${actor}` : `by ${actor}`;
  }

  const executor =
    handlers?.[activityType] || handlers["PLAYER_ADDED_TO_WATCHLIST"];

  return executor((metadata ?? {}) as ActivityMetadata) || "";
};
